import { type FormEvent, useEffect, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { AudioLines, CheckCircle2, Clock, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  validateInvitation,
  setPassword as setPasswordApi,
  type ValidateInvitationResponse,
} from '../api/invitation-api';
import { PASSWORD_RULES, getPasswordStrength, isPasswordValid } from '../utils/password-validation';

type PageState =
  | { kind: 'loading' }
  | { kind: 'set-password'; data: ValidateInvitationResponse }
  | { kind: 'existing-user'; organizationName: string }
  | { kind: 'error' }
  | { kind: 'success' };

export function SetPasswordPage() {
  const navigate = useNavigate();
  const { token } = useSearch({ from: '/set-password' });

  const [state, setState] = useState<PageState>({ kind: 'loading' });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!token) {
      setState({ kind: 'error' });
      return;
    }

    validateInvitation(token)
      .then((data) => {
        if (data.already_has_account) {
          setState({ kind: 'existing-user', organizationName: data.organization_name ?? '' });
        } else {
          setState({ kind: 'set-password', data });
        }
      })
      .catch(() => {
        setState({ kind: 'error' });
      });
  }, [token]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!isPasswordValid(password) || password !== confirm) return;

    setSubmitting(true);
    setSubmitError('');

    try {
      await setPasswordApi(token, password);
      setState({ kind: 'success' });
    } catch {
      setSubmitError('Failed to set password. The link may have expired.');
    } finally {
      setSubmitting(false);
    }
  }

  const strength = getPasswordStrength(password);
  const strengthColors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-lime-500', 'bg-green-500'];

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <AudioLines className="h-6 w-6" />
          </div>

          {state.kind === 'loading' && (
            <>
              <CardTitle className="text-2xl">ConsultNow</CardTitle>
              <div className="flex justify-center pt-4">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            </>
          )}

          {state.kind === 'set-password' && (
            <>
              <CardTitle className="text-2xl">Welcome to ConsultNow</CardTitle>
              <p className="text-sm text-muted-foreground">
                Hi {state.data.name}, you've been invited to{' '}
                <strong>{state.data.organization_name}</strong>
              </p>
            </>
          )}

          {state.kind === 'existing-user' && (
            <CardTitle className="text-2xl">ConsultNow</CardTitle>
          )}

          {state.kind === 'error' && (
            <CardTitle className="text-2xl">ConsultNow</CardTitle>
          )}

          {state.kind === 'success' && (
            <CardTitle className="text-2xl">ConsultNow</CardTitle>
          )}
        </CardHeader>

        <CardContent>
          {state.kind === 'set-password' && (
            <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
              <hr className="border-border" />
              <p className="text-sm font-medium">Create your password to get started</p>

              {submitError && (
                <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {submitError}
                </div>
              )}

              <div className="flex flex-col gap-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  autoComplete="new-password"
                  id="password"
                  required
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                {password && (
                  <div className="flex gap-1">
                    {[0, 1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded-full ${
                          i < strength ? strengthColors[Math.min(strength - 1, 4)] : 'bg-muted'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="confirm">Confirm Password</Label>
                <Input
                  autoComplete="new-password"
                  id="confirm"
                  required
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
                {confirm && password !== confirm && (
                  <p className="text-xs text-destructive">Passwords do not match</p>
                )}
              </div>

              <div className="flex flex-col gap-1 text-xs">
                {PASSWORD_RULES.map((rule) => (
                  <span
                    key={rule.label}
                    className={rule.test(password) ? 'text-green-600' : 'text-muted-foreground'}
                  >
                    {rule.test(password) ? '\u2713' : '\u2717'} {rule.label}
                  </span>
                ))}
              </div>

              <Button
                className="w-full"
                disabled={submitting || !isPasswordValid(password) || password !== confirm}
                type="submit"
              >
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Set Password
              </Button>
            </form>
          )}

          {state.kind === 'existing-user' && (
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-50">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              </div>
              <div className="text-center">
                <h3 className="font-semibold">
                  You've been added to {state.organizationName}
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  You already have a ConsultNow account.
                </p>
                <p className="text-sm text-muted-foreground">
                  Log in with your existing credentials to access the organization.
                </p>
              </div>
              <Button className="w-full" onClick={() => navigate({ to: '/login' })}>
                Go to Login
              </Button>
            </div>
          )}

          {state.kind === 'error' && (
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50">
                <Clock className="h-6 w-6 text-red-500" />
              </div>
              <div className="text-center">
                <h3 className="font-semibold">Invitation Expired</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  This invitation link is no longer valid. It may have expired or been revoked.
                </p>
                <p className="text-sm text-muted-foreground">
                  Please contact your administrator to request a new invitation.
                </p>
              </div>
              <Button variant="outline" className="w-full" onClick={() => navigate({ to: '/login' })}>
                Go to Login
              </Button>
            </div>
          )}

          {state.kind === 'success' && (
            <div className="flex flex-col items-center gap-4 py-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-50">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              </div>
              <div className="text-center">
                <h3 className="font-semibold">Password Set Successfully</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  You can now log in with your new password.
                </p>
              </div>
              <Button className="w-full" onClick={() => navigate({ to: '/login' })}>
                Go to Login
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
